import { useEffect } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { appCopy } from '@zibanu/shared';
import { OfflineError } from '@zibanu/client';
import { Button, EmptyState, OrgSwitcher } from '@zibanu/ui';
import { AppShell } from './AppShell';
import { useDeps } from './deps';
import { loginFlow } from './loginFlow';
import { useForgetOtherBooks } from './offline';
import { useBooks, useMe } from './session';

/** Nothing on screen yet: the wordmark, quietly. */
export function Splash() {
  return <div className="zw-splash">{appCopy.wordmark()}</div>;
}

/** The switcher for screens that stop at the books (upgrade, paused). */
function BooksSwitcher() {
  const me = useMe().data;
  const { current, choose } = useBooks();
  if (!me || me.memberships.length < 2) return null;
  return (
    <OrgSwitcher
      orgs={me.memberships.map((m) => ({ id: m.orgId, name: m.label, role: appCopy.role(m.role) }))}
      {...(current ? { value: current.orgId } : {})}
      onChange={choose}
    />
  );
}

/** The app comes with Business (phase 1 D1): the same words at the gate, after a code, and from a chat link. */
export function UpgradeNotice() {
  const { platform } = useDeps();
  return (
    <>
      <h1 className="zw-h1">{appCopy.upgradeTitle()}</h1>
      <p className="zw-lead">{appCopy.upgradeLead()}</p>
      <Button size="lg" block icon="message-circle" onClick={platform.openWhatsApp}>
        {appCopy.messageUs()}
      </Button>
    </>
  );
}

/** Who you are, which books, and whether the app is part of their plan (spec 3A §6.2, §7.10). */
export function Gate() {
  const me = useMe();
  const { current } = useBooks();
  const { platform } = useDeps();
  // From the gate, so books the person lost are dropped even where the shell never mounts (no books, paused, upgrade).
  useForgetOtherBooks();
  const navigate = useNavigate();
  const here = useRouterState({ select: (s) => s.location.href });
  useEffect(() => {
    if (me.data !== null) return;
    // The session ended here: come back to this screen after logging in.
    // Already heading to login (this effect runs again as the path changes): the place was remembered once.
    if (!here.startsWith('/login')) loginFlow.rememberPlace(here);
    void navigate({ to: '/login', replace: true });
  }, [me.data, navigate, here]);

  if (me.data === undefined) {
    // Nothing saved on this phone yet: offline says so; anything else is ours.
    if (me.isPending && me.fetchStatus !== 'paused') return <Splash />;
    const offline = me.fetchStatus === 'paused' || me.error instanceof OfflineError;
    return <EmptyState title={offline ? appCopy.offlineTitle() : appCopy.ourError()} action={appCopy.tryAgain()} onAction={() => void me.refetch()} />;
  }
  if (!me.data) return <Splash />;
  if (me.data.memberships.length === 0) {
    return (
      <main className="zw-stop">
        <EmptyState title={appCopy.noBooksYet()} action={appCopy.messageUs()} onAction={platform.openWhatsApp} />
      </main>
    );
  }
  if (!current) return <Splash />;
  if (current.status === 'frozen') {
    return (
      <main className="zw-stop">
        <BooksSwitcher />
        <EmptyState title={appCopy.booksPaused()} action={appCopy.messageUs()} onAction={platform.openWhatsApp} />
      </main>
    );
  }
  if (!current.entitlements.includes('app')) {
    return (
      <main className="zw-stop">
        <BooksSwitcher />
        <UpgradeNotice />
      </main>
    );
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
